import React, { useCallback, useEffect, useState, useRef, Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UsersIcon, ActivityIcon, HeartIcon, MedalIcon, TargetIcon, ZapIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { FeatureCard } from '../components/FeatureCard';
import { ActivityCard } from '../components/ActivityCard';
import { ValuesSection } from '../components/ValuesSection';
import { CoachCard, Coach } from '../components/CoachCard';
import { CoachModal } from '../components/CoachModal';
import { coaches } from '../data/coaches';
export function HomePage() {
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [expandedAnnouncements, setExpandedAnnouncements] = useState<number[]>([]);
  const carouselRef = useRef<HTMLDivElement>(null);
  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      if (carouselRef.current) {
        const {
          scrollLeft,
          scrollWidth,
          clientWidth
        } = carouselRef.current;
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          carouselRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          scrollCarousel('right');
        }
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [scrollCarousel]);
  const toggleAnnouncement = (index: number) => {
    setExpandedAnnouncements(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };
  const announcements = [{
    date: '15 April 2026',
    title: 'Junior Advanced Programme Launching',
    summary: 'A new elite-level programme for junior county players.',
    details: 'Our Bright Star Sports Junior Advanced programme launches in September 2026 in Derbyshire. Designed for players selected onto the Junior County Programme, these 90-minute sessions are led by UKCC Level 2 Coaches and bridge the gap to county-level training. Register your interest on our Badminton page.',
    accentColor: 'bg-brand-blue',
    link: '/badminton'
  }, {
    date: '1 March 2026',
    title: 'Hampshire Sessions Now Open',
    summary: 'Bright Star Sports expands to Hampshire with new weekly sessions.',
    details: 'We are thrilled to announce the launch of Bright Star Sports sessions in Hampshire. Weekly badminton and multi-sports classes are now available for booking. Visit our Book Now page to find sessions near you.',
    accentColor: 'bg-brand-green',
    link: '/book'
  }, {
    date: '10 January 2026',
    title: 'New Medals Programme for 2026',
    summary: 'Updated Olympic Values medals with Bronze, Silver and Gold progression.',
    details: 'Our popular medals programme has been refreshed for 2026. Children can now progress through all seven Olympic Values medals and then earn Bronze, Silver, and Gold medals recognising their continued commitment to the values both on and off the court.',
    accentColor: 'bg-brand-yellow',
    link: '/medals'
  }];
  const carouselImages = [{
    src: 'https://cdn.magicpatterns.com/uploads/h1HjTQtx6uRzRJncSSv8d2/badminton.webp',
    alt: 'Bright Star Sports badminton session'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/nius5mV4GSJtkuCgu886pQ/Multisports.webp',
    alt: 'Bright Star Sports multi-sports session'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/4gCkFMntbwDcZHYhPNEDey/Netball.jpg',
    alt: 'Bright Star Sports netball session'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp',
    alt: 'Bright Star Sports group coaching'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/8m3ZDfLxFh7GqHfUAhVAku/Adults-Badminton-Photo-TK-LK-scaled-e1737371933423_(1).jpg',
    alt: 'Bright Star Sports adult badminton'
  }];
  const features = [{
    icon: <ActivityIcon className="w-6 h-6" data-id="element-935" />,
    title: 'Multi-Sport Development',
    description: 'Exposure to multiple sports builds well-rounded athletes with diverse skills.'
  }, {
    icon: <UsersIcon className="w-6 h-6" data-id="element-936" />,
    title: 'Confidence & Friendships',
    description: 'Our sessions foster self-belief, teamwork, and lasting friendships.'
  }, {
    icon: <ZapIcon className="w-6 h-6" data-id="element-937" />,
    title: 'Motor Skills',
    description: 'Age-appropriate activities develop coordination, agility, and physical literacy.'
  }, {
    icon: <MedalIcon className="w-6 h-6" data-id="element-938" />,
    title: 'Expert-Led Programmes',
    description: 'Every session designed and delivered by expert coaches who have competed at an elite level.'
  }, {
    icon: <TargetIcon className="w-6 h-6" data-id="element-939" />,
    title: 'Coaching for Every Age',
    description: 'Tailored programmes from early years through to competitive juniors.'
  }, {
    icon: <HeartIcon className="w-6 h-6" data-id="element-940" />,
    title: 'Healthy Body & Mind',
    description: 'Building lifelong habits for physical and mental wellbeing.'
  }];
  const activities = [{
    title: 'Badminton',
    description: 'From first racket to competitive play, our Olympian-led badminton programme develops technique, agility, and game sense.',
    slug: 'badminton',
    colorAccent: 'blue' as const,
    icon: <ActivityIcon className="w-32 h-32" data-id="element-941" />,
    image: 'https://cdn.magicpatterns.com/uploads/h1HjTQtx6uRzRJncSSv8d2/badminton.webp'
  }, {
    title: 'Multi-Sports',
    description: 'A dynamic mix of sports and activities designed to build all-round athletic ability and a love of movement.',
    slug: 'multi-sports',
    colorAccent: 'coral' as const,
    icon: <TargetIcon className="w-32 h-32" data-id="element-942" />,
    image: 'https://cdn.magicpatterns.com/uploads/nius5mV4GSJtkuCgu886pQ/Multisports.webp'
  }, {
    title: 'Netball',
    description: 'Build court skills, tactical awareness, and team spirit in our inclusive netball sessions for all abilities.',
    slug: 'netball',
    colorAccent: 'green' as const,
    icon: <UsersIcon className="w-32 h-32" data-id="element-943" />,
    image: 'https://cdn.magicpatterns.com/uploads/4gCkFMntbwDcZHYhPNEDey/Netball.jpg'
  }];
  return <div className="w-full overflow-hidden pt-20" data-id="element-944">
      {/* SECTION 1: HERO */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] bg-brand-blue overflow-hidden" data-id="element-945">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-946" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-947"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-948">
          <div className="max-w-3xl" data-id="element-949">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 leading-tight text-[#C33149]" data-id="element-950">
              <span className="text-[#00A650]" data-id="element-951">Inspire.</span> Encourage.{' '}
              <span className="text-brand-yellow" data-id="element-952">Achieve.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl leading-relaxed" data-id="element-953">
              Expert sport coaching led by our Olympian coaches Donna Kellogg
              MBE, Natalie Barton and Sarah Chapman, building confidence,
              skills, and friendships for young athletes across the UK.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10" data-id="element-954">
              <Button href="/book" variant="primary" size="lg" icon data-id="element-955">
                Book a Session
              </Button>
              <Button onClick={() => document.getElementById('mission')?.scrollIntoView({
              behavior: 'smooth'
            })} variant="secondary" size="lg" className="border-white text-white hover:bg-white/20" data-id="element-956">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: MISSION / BENEFITS */}
      <section id="mission" className="py-24 bg-navy-50" data-id="element-957">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-958">
          <SectionHeader eyebrow="Our Mission" title="From Grassroots to Elite" description="We believe every child deserves access to world-class coaching. Our programmes are designed to develop not just better athletes, but confident, resilient young people." data-id="element-959" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-960">
            {features.map((feature, index) => <FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description} index={index} data-id="element-961" />)}
          </div>
        </div>
      </section>

      {/* SECTION 3: ACTIVITIES */}
      <section className="py-24 bg-white" data-id="element-962">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-963">
          <SectionHeader eyebrow="What We Offer" title="Our Programmes" description="Discover our range of expertly designed coaching sessions, tailored for different age groups and skill levels." data-id="element-964" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-965">
            {activities.map((activity, index) => <ActivityCard key={index} {...activity} index={index} data-id="element-966" />)}
          </div>
        </div>
      </section>

      {/* SECTION 3.5: COMPANY ANNOUNCEMENTS */}
      <section className="py-24 bg-white border-t border-gray-100" data-id="element-967">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-968">
          <SectionHeader eyebrow="Latest News" title="Announcements" description="Stay up to date with the latest programmes, locations, and news from Bright Star Sports." data-id="element-969" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-970">
            {announcements.map((announcement, index) => {
            const isExpanded = expandedAnnouncements.includes(index);
            return <div key={index} className="bg-navy-50 border border-navy-100 rounded-2xl overflow-hidden shadow-sm flex flex-col h-fit" data-id="element-971">
                  <div className={`h-1.5 w-full ${announcement.accentColor}`} data-id="element-972"></div>
                  <div className="p-6 md:p-8" data-id="element-973">
                    <span className="text-sm font-bold text-navy-500 uppercase tracking-wider mb-3 block" data-id="element-974">
                      {announcement.date}
                    </span>
                    <h3 className="text-xl font-display font-bold text-navy-900 mb-3 leading-tight" data-id="element-975">
                      {announcement.title}
                    </h3>
                    <p className="text-navy-600 mb-6" data-id="element-976">{announcement.summary}</p>

                    <button onClick={() => toggleAnnouncement(index)} className="flex items-center text-sm font-bold text-brand-blue hover:text-brand-red transition-colors group w-full justify-between" data-id="element-977">
                      <span data-id="element-978">
                        {isExpanded ? 'Show less' : 'Read full announcement'}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-979" />
                    </button>

                    <AnimatePresence data-id="element-980">
                      {isExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0,
                    marginTop: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1,
                    marginTop: 24
                  }} exit={{
                    height: 0,
                    opacity: 0,
                    marginTop: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-981">
                          <div className="pt-6 border-t border-navy-200" data-id="element-982">
                            <p className="text-navy-700 leading-relaxed mb-4" data-id="element-983">
                              {announcement.details}
                            </p>
                            <Link to={announcement.link} className="inline-flex items-center text-sm font-bold text-brand-blue hover:text-brand-red transition-colors" data-id="element-984">
                              Learn more &rarr;
                            </Link>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* SECTION 3.6: IMAGE CAROUSEL */}
      <section className="py-16 bg-navy-50 border-t border-navy-100 relative group" data-id="element-985">
        <div ref={carouselRef} className="flex overflow-x-auto gap-4 md:gap-6 px-4 sm:px-6 lg:px-8 pb-8 snap-x snap-mandatory hide-scrollbar" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-986">
          {/* Double the images to create an infinite loop effect visually */}
          {[...carouselImages, ...carouselImages].map((image, index) => <div key={index} className="min-w-[85vw] md:min-w-[400px] lg:min-w-[500px] h-[250px] md:h-[350px] flex-shrink-0 snap-center rounded-xl overflow-hidden shadow-md" data-id="element-987">
              <img src={image.src} alt={image.alt} className="w-full h-full object-cover" loading="lazy" data-id="element-988" />
            </div>)}
        </div>

        {/* Navigation Buttons */}
        <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-navy-50 to-transparent pointer-events-none hidden md:block" data-id="element-989"></div>
        <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-navy-50 to-transparent pointer-events-none hidden md:block" data-id="element-990"></div>

        <button onClick={() => scrollCarousel('left')} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white text-navy-900 rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" aria-label="Previous image" data-id="element-991">
          <ChevronLeftIcon className="w-6 h-6" data-id="element-992" />
        </button>

        <button onClick={() => scrollCarousel('right')} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white text-navy-900 rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" aria-label="Next image" data-id="element-993">
          <ChevronRightIcon className="w-6 h-6" data-id="element-994" />
        </button>
      </section>

      {/* SECTION 4: OLYMPIC VALUES */}
      <ValuesSection ctaLabel="Discover Our Medals Programme" data-id="element-995" />

      {/* SECTION 5: MEET THE TEAM */}
      <section className="py-24 bg-navy-50" data-id="element-996">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-997">
          <SectionHeader eyebrow="Our Coaches" title="Meet the Team" description="Learn from the best. Our lead coaches Donna Kellogg MBE, Natalie Barton and Sarah Chapman are all Team GB Olympians dedicated to inspiring the next generation through sport." data-id="element-998" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-999">
            {coaches.map((coach, index) => <CoachCard key={coach.id} coach={coach} index={index} onViewProfile={setSelectedCoach} data-id="element-1000" />)}
          </div>
        </div>
      </section>

      {/* Coach Modal Portal */}
      <CoachModal coach={selectedCoach} isOpen={!!selectedCoach} onClose={() => setSelectedCoach(null)} data-id="element-1001" />
    </div>;
}